import type { ReactElement } from 'react';
import { useState } from 'react';
import type { NoteTemplate, TemplateSection } from '@contracts/templates';
import { useOrg, useTemplateActions, useTemplates } from '../api/queries';
import {
  Broken,
  Button,
  Card,
  Confirm,
  Empty,
  Field,
  Modal,
  Rows,
  Picker,
  SkeletonRows,
  TextArea,
  useNotices,
} from '../ui';

/** What the editor holds: a template being made, or one being changed. */
interface Draft {
  id: string | null;
  name: string;
  description: string;
  instructions: string;
  sections: TemplateSection[];
  scope: 'personal' | 'workspace';
}

const BLANK: Draft = {
  id: null,
  name: '',
  description: '',
  instructions: '',
  sections: [{ title: '', prompt: '' }],
  scope: 'personal',
};

const MAX_SECTIONS = 12;

/**
 * Templates (PRD 61): the shapes a meeting can be written up in — beside its
 * full note, never instead of it.
 *
 * Aleksandr at the AI Talks: *"can it be prompted to send specific summaries by
 * your prompt to a specific place?"* A template is what the write-up is for,
 * and its sections, each with what goes under it. The built-in ones are to be
 * used or duplicated; your own are yours or everybody's in the workspace.
 */
export function Templates(): ReactElement {
  const templates = useTemplates();
  const org = useOrg();
  const actions = useTemplateActions();
  const { show } = useNotices();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [looking, setLooking] = useState<NoteTemplate | null>(null);
  const [deleting, setDeleting] = useState<NoteTemplate | null>(null);
  const workspace = org.data?.name ?? 'the workspace';

  const duplicate = (t: NoteTemplate) => {
    setLooking(null);
    setDraft({
      id: null,
      name: `${t.name} (copy)`.slice(0, 80),
      description: t.description,
      instructions: t.instructions,
      sections: t.sections.map((s) => ({ ...s })),
      scope: 'personal',
    });
  };
  const edit = (t: NoteTemplate) =>
    setDraft({
      id: t.id,
      name: t.name,
      description: t.description,
      instructions: t.instructions,
      sections: t.sections.map((s) => ({ ...s })),
      scope: t.kind === 'workspace' ? 'workspace' : 'personal',
    });

  const all = templates.data ?? [];
  const groups: Array<[string, NoteTemplate[], string]> = [
    ['Built in', all.filter((t) => t.kind === 'stock'), ''],
    [
      `Shared in ${workspace}`,
      all.filter((t) => t.kind === 'workspace'),
      'Nothing shared yet. A template saved for everyone in the workspace lands here.',
    ],
    [
      'Only you',
      all.filter((t) => t.kind === 'personal'),
      'Nothing of your own yet. Start from a built-in one with Duplicate, or make a new one.',
    ],
  ];

  return (
    <div className="page stack-lg">
      <div className="cluster" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="t-title">Templates</h1>
          <p className="mono">
            the shapes a meeting can be written up in — beside its full note, never instead of it
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setDraft({ ...BLANK, sections: [{ title: '', prompt: '' }] })}
        >
          New template
        </Button>
      </div>

      {templates.isPending ? (
        <Card flush>
          <SkeletonRows rows={6} />
        </Card>
      ) : templates.isError ? (
        <Broken what="The templates" retry={() => void templates.refetch()} />
      ) : (
        groups.map(([title, list, none]) => (
          <Card key={title} title={title} flush>
            {list.length === 0 ? (
              <Empty lead={none} />
            ) : (
              <Rows>
                {list.map((t) => (
                  <div key={t.id} className="row template-row">
                    <span className="grow stack" style={{ gap: 2, minWidth: 0 }}>
                      <span className="t-heading">{t.name}</span>
                      {t.description ? <span className="t-secondary">{t.description}</span> : null}
                      <span className="mono">
                        {t.sections.length} {t.sections.length === 1 ? 'section' : 'sections'}:{' '}
                        {t.sections.map((s) => s.title).join(' · ')}
                        {t.owner && t.kind === 'workspace'
                          ? ` · by ${t.owner.name ?? t.owner.email}`
                          : ''}
                      </span>
                    </span>
                    <span className="cluster" style={{ gap: 6, flex: 'none' }}>
                      {t.canEdit ? (
                        <Button size="sm" onClick={() => edit(t)}>
                          Edit
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => setLooking(t)}>
                          Look
                        </Button>
                      )}
                      <Button size="sm" variant="quiet" onClick={() => duplicate(t)}>
                        Duplicate
                      </Button>
                      {t.canEdit ? (
                        <Button size="sm" variant="quiet" onClick={() => setDeleting(t)}>
                          Delete
                        </Button>
                      ) : null}
                    </span>
                  </div>
                ))}
              </Rows>
            )}
          </Card>
        ))
      )}

      {draft ? (
        <TemplateEditor draft={draft} workspace={workspace} onClose={() => setDraft(null)} />
      ) : null}
      {looking ? (
        <Modal
          title={looking.name}
          onClose={() => setLooking(null)}
          foot={
            <>
              <Button variant="quiet" onClick={() => setLooking(null)}>
                Close
              </Button>
              <Button variant="primary" onClick={() => duplicate(looking)}>
                Duplicate to change it
              </Button>
            </>
          }
        >
          <div className="stack">
            {looking.instructions ? (
              <p>
                <span className="field-label">What it is for</span>
                <br />
                {looking.instructions}
              </p>
            ) : null}
            {looking.sections.map((s, i) => (
              <div key={`${i}-${s.title}`} className="stack" style={{ gap: 2 }}>
                <span className="t-heading">{s.title}</span>
                <span className="t-secondary">{s.prompt}</span>
              </div>
            ))}
          </div>
        </Modal>
      ) : null}
      {deleting ? (
        <Confirm
          title={`Delete “${deleting.name}”?`}
          noun="Delete the template"
          working={actions.remove.isPending}
          onClose={() => setDeleting(null)}
          onConfirm={() =>
            actions.remove.mutate(deleting.id, {
              onSuccess: () => {
                setDeleting(null);
                show({ tone: 'success', text: 'Deleted. Write-ups already made with it stay.' });
              },
              onError: (e) => show({ tone: 'failure', text: e.message }),
            })
          }
        >
          Write-ups already made with it stay as they are, and a series set to use it stops writing
          with it.
        </Confirm>
      ) : null}
    </div>
  );
}

/** Making one, or changing one: what it is for, and its sections, in order. */
function TemplateEditor({
  draft: initial,
  workspace,
  onClose,
}: {
  draft: Draft;
  workspace: string;
  onClose: () => void;
}): ReactElement {
  const [d, setD] = useState<Draft>(initial);
  const actions = useTemplateActions();
  const { show } = useNotices();
  const saving = actions.create.isPending || actions.update.isPending;
  const sections = d.sections.map((s) => ({ title: s.title.trim(), prompt: s.prompt.trim() }));
  const why = !d.name.trim()
    ? 'It needs a name'
    : sections.length === 0 || sections.some((s) => !s.title)
      ? 'Every section needs a title'
      : null;
  const set = (i: number, patch: Partial<TemplateSection>) =>
    setD((was) => ({
      ...was,
      sections: was.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)),
    }));
  const move = (i: number, by: number) =>
    setD((was) => {
      const next = [...was.sections];
      const [s] = next.splice(i, 1);
      next.splice(i + by, 0, s);
      return { ...was, sections: next };
    });
  const save = () => {
    const body = {
      name: d.name.trim(),
      description: d.description.trim(),
      instructions: d.instructions.trim(),
      sections,
      scope: d.scope,
    };
    const done = {
      onSuccess: () => {
        show({ tone: 'success', text: `“${body.name}” saved.` });
        onClose();
      },
      onError: (e: Error) => show({ tone: 'failure', text: e.message }),
    };
    if (d.id) actions.update.mutate({ id: d.id, body }, done);
    else actions.create.mutate(body, done);
  };

  return (
    <Modal
      title={d.id ? 'Change the template' : 'New template'}
      onClose={onClose}
      foot={
        <>
          {why ? <span className="t-secondary grow">{why}</span> : <span className="grow" />}
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!!why}
            working={saving ? 'Saving…' : false}
            onClick={save}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="stack template-editor">
        <Field
          label="Name"
          value={d.name}
          maxLength={80}
          placeholder="Sprint digest"
          onChange={(e) => setD({ ...d, name: e.target.value })}
        />
        <Field
          label="One line about it"
          value={d.description}
          maxLength={240}
          placeholder="What shipped and what is next, for #sprint"
          onChange={(e) => setD({ ...d, description: e.target.value })}
        />
        <Picker
          label="Who can use it"
          hint={
            d.scope === 'personal'
              ? 'Nobody else sees or uses the template. What you write with it on a meeting is seen by everyone who can read that meeting. A series can only use shared templates.'
              : 'Everyone in the workspace sees and uses it; you and the administrators can change it.'
          }
          value={d.scope}
          onChange={(scope) => setD({ ...d, scope })}
          width="100%"
          options={[
            { value: 'personal' as const, label: 'Only me' },
            { value: 'workspace' as const, label: `Everyone in ${workspace}` },
          ]}
        />
        <TextArea
          label="What it is for"
          value={d.instructions}
          maxLength={4000}
          rows={3}
          placeholder="Who reads it, how long, in what tone — “For the sprint channel. Under 150 words. Names, never ‘the team’.”"
          onChange={(e) => setD({ ...d, instructions: e.target.value })}
        />
        <span className="field-label">Sections, in order</span>
        {d.sections.map((s, i) => (
          <div key={i} className="section-card stack">
            <div className="cluster" style={{ gap: 6 }}>
              <span className="mono">{i + 1}</span>
              <span className="grow">
                <Field
                  aria-label={`Section ${i + 1} title`}
                  value={s.title}
                  maxLength={120}
                  placeholder="Who does what"
                  onChange={(e) => set(i, { title: e.target.value })}
                />
              </span>
              <Button
                size="sm"
                variant="quiet"
                disabled={i === 0}
                aria-label="Move up"
                onClick={() => move(i, -1)}
              >
                ↑
              </Button>
              <Button
                size="sm"
                variant="quiet"
                disabled={i === d.sections.length - 1}
                aria-label="Move down"
                onClick={() => move(i, 1)}
              >
                ↓
              </Button>
              <Button
                size="sm"
                variant="quiet"
                disabled={d.sections.length === 1}
                aria-label="Remove the section"
                onClick={() => setD({ ...d, sections: d.sections.filter((_, j) => j !== i) })}
              >
                ✕
              </Button>
            </div>
            <TextArea
              aria-label={`Section ${i + 1} instructions`}
              value={s.prompt}
              maxLength={2000}
              rows={2}
              placeholder="What goes here, in what form, and when to leave it out — “One line per commitment, owner first. Leave the section out if nobody committed to anything.”"
              onChange={(e) => set(i, { prompt: e.target.value })}
            />
          </div>
        ))}
        <span>
          <Button
            size="sm"
            disabled={d.sections.length >= MAX_SECTIONS}
            onClick={() => setD({ ...d, sections: [...d.sections, { title: '', prompt: '' }] })}
          >
            Add a section
          </Button>
        </span>
      </div>
    </Modal>
  );
}
